import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  CAMPAIGN_PHASE_LABEL,
  getCampaignPhase,
} from "@repo/components/src/utils/campaignPhase";
import { CampaignMediaFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import { useNow } from "@/hooks/useNow";
import CampaignPlaceholder from "@/assets/images/campaign-placeholder.webp";
import React, { useMemo } from "react";
import { ReportCampaign } from "./types";
import { formatDateLong } from "./utils";

type Props = {
  campaign: ReportCampaign;
  medias: readonly CampaignMediaFragmentFragment[];
  actions?: React.ReactNode;
};

const Hero = React.memo(({ campaign, medias, actions }: Props) => {
  const now = useNow(undefined, false);
  const phase = getCampaignPhase(
    {
      status: campaign.status.enum,
      startsAt: campaign.startsAt,
      endsAt: campaign.endsAt,
    },
    now,
  );
  const isActive = phase === "accepting";
  const statusLabel = CAMPAIGN_PHASE_LABEL[phase];

  const firstMediaUrl = useMemo(() => {
    if (!medias.length) return null;
    const sorted = [...medias].sort((a, b) => a.order - b.order);
    const file = sorted[0]?.media;
    return file?.url ?? null;
  }, [medias]);

  return (
    <Card variant="outline" className="rounded-2xl" size="lg">
      <HStack space="lg" className="items-center">
        <Box className="w-20 h-20 rounded-xl overflow-hidden bg-background-100 items-center justify-center">
          <Image
            source={
              firstMediaUrl ? { uri: firstMediaUrl } : CampaignPlaceholder
            }
            alt={campaign.title}
            size="none"
            className="w-full h-full"
            contentFit="cover"
            contentPosition="center"
          />
        </Box>
        <VStack className="flex-1" space="xs">
          <Heading size="lg" className="text-typography-900">
            {campaign.title}
          </Heading>
          <HStack space="sm" className="items-center flex-wrap">
            <Box
              className={
                isActive
                  ? "bg-brand-50 px-3 py-1 rounded-full"
                  : "bg-background-100 px-3 py-1 rounded-full"
              }
            >
              <HStack space="xs" className="items-center">
                <Box
                  className={
                    isActive
                      ? "w-1.5 h-1.5 rounded-full bg-brand-500"
                      : "w-1.5 h-1.5 rounded-full bg-typography-400"
                  }
                />
                <Text
                  size="xs"
                  className={
                    isActive
                      ? "text-brand-700 font-semibold"
                      : "text-typography-500 font-semibold"
                  }
                >
                  {statusLabel}
                </Text>
              </HStack>
            </Box>
            <Text size="sm" className="text-typography-500">
              {formatDateLong(campaign.startDate) ?? "—"}
              {" → "}
              {formatDateLong(campaign.endDate) ?? "Sin fecha límite"}
            </Text>
            <Box className="w-1 h-1 rounded-full bg-outline-200" />
            <Text size="sm" className="text-typography-500">
              {campaign.campaignType?.display ?? "—"}
            </Text>
          </HStack>
        </VStack>
        {actions ? <Box>{actions}</Box> : null}
      </HStack>
    </Card>
  );
});

Hero.displayName = "ReportHero";

export default Hero;
